import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import { FilledDeleteIcon, CircleCheckIcon } from '../../Icons';
import { useTextInputStyles, CustomColor } from './useTextInputStyles';

export type InputHookConfig = {
  /** If true, the input will update completly in onChange, rather than waiting for onBlur */
  greedyCheck?: boolean;
};

export const emptyDecoration = <div />;

type Props = {
  error: boolean;
  defaultValue: string;
  customFocus?: () => void;
  customBlur?: () => void;
  customColor?: CustomColor;
  loading?: boolean;
  config?: InputHookConfig;
};

export default function useDefaultInputHook({
  error,
  defaultValue,
  customColor,
  loading = false,
  config = { greedyCheck: false },
  customFocus,
  customBlur,
}: Props): {
  currentClass: string;
  decoration: JSX.Element;
  onBlur: () => void;
  onFocus: () => void;
  hasDecoration: boolean;
} {
  const classes = useTextInputStyles(customColor);
  const [currentClass, updateCurrentClass] = useState<string>(
    classes.defaultCustomInput,
  );
  const [focused, toggleFocused] = useState(false);
  const [decoration, setDecoration] = useState<JSX.Element>(emptyDecoration);
  const [hasDecoration, toggleHasDecoration] = useState(false);
  const [isFirstRender, toggleIsFirstRender] = useState(true);
  const [renderedWithError, toggleRenderedWithError] = useState(false);

  const deleteDecoration = useMemo((): JSX.Element => {
    return customColor ? (
      <FilledDeleteIcon
        data-testid="deleteIcon"
        colorBack={customColor.font}
        colorIcon={customColor.icon}
      />
    ) : (
      <FilledDeleteIcon data-testid="deleteIcon" />
    );
  }, [customColor]);

  const successDecoration = useMemo((): JSX.Element => {
    return customColor ? (
      <CircleCheckIcon
        data-testid="checkIcon"
        colorCircle={customColor.font}
        colorIcon={customColor.icon}
      />
    ) : (
      <CircleCheckIcon data-testid="checkIcon" />
    );
  }, [customColor]);

  const onBlur = useCallback(() => {
    toggleRenderedWithError(error);

    // Call custom blur before applying decorations.
    if (customBlur) {
      customBlur();
    }

    if (error) {
      setDecoration(deleteDecoration);
      updateCurrentClass(classes.errorCustomInput);
    } else if (defaultValue !== '') {
      setDecoration(successDecoration);
      updateCurrentClass(classes.correctCustomInput);
    } else {
      setDecoration(emptyDecoration);
      updateCurrentClass(classes.defaultCustomInput);
    }
    toggleFocused(false);
  }, [error, defaultValue]);

  const onFocus = useCallback(() => {
    toggleFocused(true);
    if (customFocus) {
      customFocus();
    }
  }, []);

  const usedDecoration = useMemo(() => {
    return loading ? <CircularProgress size={12} /> : decoration;
  }, [loading, decoration]);

  useEffect(() => {
    toggleHasDecoration(decoration !== emptyDecoration || loading);
  }, [decoration, loading]);

  useEffect(() => {
    if (config.greedyCheck || isFirstRender || error) {
      onBlur();
    } else if (defaultValue !== '') {
      if (focused) {
        setDecoration(deleteDecoration);
      }
      if (renderedWithError) {
        toggleRenderedWithError(false);
        updateCurrentClass(classes.defaultCustomInput);
      }
    } else {
      setDecoration(emptyDecoration);
    }
    toggleIsFirstRender(false);
  }, [defaultValue, error]);

  return {
    currentClass,
    decoration: usedDecoration,
    onBlur,
    onFocus,
    hasDecoration,
  };
}
